import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getDeal, createDeal, updateDeal, getTeamMembers, getContacts, deleteDeal } from '../utils/api';

const STATUSES = [
  { value: 'active', label: 'Active' },
  { value: 'under_contract', label: 'Under Contract' },
  { value: 'closed', label: 'Closed' },
  { value: 'dead', label: 'Dead' },
];

const ROLES = [
  { value: 'equity', label: 'Equity' },
  { value: 'debt', label: 'Debt' },
  { value: 'broker', label: 'Broker' },
  { value: 'service', label: 'Service' },
];

const EMPTY = {
  name: '', address: '', status: 'active',
  whisper_price: '', underwriting_price: '', purchase_price: '', equity_amount: '',
  close_date: '', relationship_driver: '', notes: '',
  contacts: [], // [{ id, role }]
};

export default function DealFormPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;

  const [form, setForm] = useState(EMPTY);
  const [teamMembers, setTeamMembers] = useState([]);
  const [allContacts, setAllContacts] = useState([]);
  const [contactSearch, setContactSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);

  useEffect(() => {
    Promise.all([
      getTeamMembers().then(r => {
        const members = r.data || r;
        setTeamMembers(members);
        return members;
      }),
      getContacts({ per_page: 200 }).then(r => { setAllContacts(r.data || []); }),
      isEdit ? getDeal(id).then(deal => setForm({
        name: deal.name, address: deal.address, status: deal.status,
        whisper_price: deal.whisper_price || '',
        underwriting_price: deal.underwriting_price || '',
        purchase_price: deal.purchase_price || '',
        equity_amount: deal.equity_amount || '',
        close_date: deal.close_date || '',
        relationship_driver: deal.relationship_driver || '',
        notes: deal.notes || '',
        contacts: (deal.contacts || []).map(c => ({ id: c.id, role: c.role || 'equity' })),
      })) : Promise.resolve(),
    ])
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [id, isEdit]);

  const set = (field, val) => setForm(f => ({ ...f, [field]: val }));

  const toggleContact = (contact) => {
    setForm(f => {
      const existing = f.contacts.find(c => c.id === contact.id);
      if (existing) {
        return { ...f, contacts: f.contacts.filter(c => c.id !== contact.id) };
      }
      return { ...f, contacts: [...f.contacts, { id: contact.id, role: 'equity' }] };
    });
  };

  const setContactRole = (contactId, role) => {
    setForm(f => ({
      ...f,
      contacts: f.contacts.map(c => c.id === contactId ? { ...c, role } : c),
    }));
  };

  const handleSubmit = async () => {
    if (!form.name || !form.address) {
      setError('Name and address are required.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      if (isEdit) {
        await updateDeal(id, form);
        navigate(`/deals/${id}`);
      } else {
        const created = await createDeal(form);
        navigate(`/deals/${created.id}`);
      }
    } catch (e) {
      setError(e.message);
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this deal permanently? This cannot be undone.')) return;
    try {
      await deleteDeal(id);
      navigate('/deals');
    } catch (e) {
      setError(e.message);
    }
  };

  const filteredContacts = allContacts.filter(c => {
    if (!contactSearch) return true;
    const q = contactSearch.toLowerCase();
    return `${c.first_name} ${c.last_name} ${c.organization || ''}`.toLowerCase().includes(q);
  });

  const selectedIds = new Set(form.contacts.map(c => c.id));

  if (loading) return <div className="page-loading">Loading…</div>;

  return (
    <div className="page-container" style={{ maxWidth: 700 }}>
      <div className="breadcrumb">
        <span className="breadcrumb-link" onClick={() => navigate('/deals')}>Deals</span>
        <span>/</span>
        <span>{isEdit ? 'Edit Deal' : 'New Deal'}</span>
      </div>
      <h1 className="page-title">{isEdit ? 'Edit Deal' : 'New Deal'}</h1>

      {error && <div className="form-error form-error-banner">{error}</div>}

      <div className="form-card">
        {/* Core */}
        <div className="form-row">
          <div className="form-group" style={{ flex: 2 }}>
            <label className="form-label">Deal Name *</label>
            <input className="input" value={form.name} onChange={e => set('name', e.target.value)} placeholder="e.g. Riverview Retail Center" />
          </div>
          <div className="form-group" style={{ flex: '0 0 160px' }}>
            <label className="form-label">Status</label>
            <select className="input" value={form.status} onChange={e => set('status', e.target.value)}>
              {STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">Address *</label>
          <input className="input" value={form.address} onChange={e => set('address', e.target.value)} placeholder="123 Main St, Austin, TX 78701" />
        </div>

        {/* Price fields */}
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Whisper Price</label>
            <input className="input" value={form.whisper_price} onChange={e => set('whisper_price', e.target.value)} placeholder="$0" />
          </div>
          <div className="form-group">
            <label className="form-label">Underwriting Price</label>
            <input className="input" value={form.underwriting_price} onChange={e => set('underwriting_price', e.target.value)} placeholder="$0" />
          </div>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Purchase Price</label>
            <input className="input" value={form.purchase_price} onChange={e => set('purchase_price', e.target.value)} placeholder="$0" />
          </div>
          <div className="form-group">
            <label className="form-label">Equity Amount</label>
            <input className="input" value={form.equity_amount} onChange={e => set('equity_amount', e.target.value)} placeholder="$0" />
          </div>
        </div>

        {/* Meta */}
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Close Date</label>
            <input type="date" className="input" value={form.close_date} onChange={e => set('close_date', e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Relationship Driver</label>
            <select className="input" value={form.relationship_driver} onChange={e => set('relationship_driver', e.target.value)}>
              <option value="">— None —</option>
              {teamMembers.map(m => <option key={m.id} value={m.id}>{m.display_name}</option>)}
            </select>
          </div>
        </div>

        {/* Notes */}
        <div className="form-group">
          <label className="form-label">Notes</label>
          <textarea className="input" rows={3} value={form.notes} onChange={e => set('notes', e.target.value)} />
        </div>

        {/* Contacts with roles */}
        <div className="form-group">
          <label className="form-label">
            Linked Contacts
            {form.contacts.length > 0 && (
              <span className="badge badge-neutral" style={{ marginLeft: 8 }}>{form.contacts.length} selected</span>
            )}
          </label>
          <input
            className="input"
            placeholder="Search contacts…"
            value={contactSearch}
            onChange={e => setContactSearch(e.target.value)}
            style={{ marginBottom: 8 }}
          />
          <div style={{ maxHeight: 240, overflowY: 'auto', border: '1px solid var(--color-border)', borderRadius: 6 }}>
            {filteredContacts.map((c, i) => {
              const isSelected = selectedIds.has(c.id);
              const linked = form.contacts.find(x => x.id === c.id);
              return (
                <div key={c.id} style={{
                  display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px',
                  background: isSelected ? 'var(--color-hover)' : 'transparent',
                  borderBottom: i < filteredContacts.length - 1 ? '1px solid var(--color-border-faint)' : 'none',
                }}>
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => toggleContact(c)}
                    style={{ accentColor: 'var(--color-success)' }}
                  />
                  <div className={`contact-avatar contact-avatar-sm avatar-${c.category}`}>
                    {c.first_name?.[0]}{c.last_name?.[0]}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '0.875rem', color: 'var(--color-text-primary)' }}>
                      {c.first_name} {c.last_name}
                    </div>
                    {c.organization && (
                      <div style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)' }}>{c.organization}</div>
                    )}
                  </div>
                  {isSelected && (
                    <select
                      value={linked?.role || 'equity'}
                      onChange={e => setContactRole(c.id, e.target.value)}
                      className="input-sm"
                      style={{ width: 90 }}
                      onClick={e => e.stopPropagation()}
                    >
                      {ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                    </select>
                  )}
                </div>
              );
            })}
            {filteredContacts.length === 0 && (
              <div style={{ padding: 12, color: 'var(--color-text-muted)', fontSize: '0.85rem' }}>No contacts found</div>
            )}
          </div>
        </div>
      </div>

      <div className="form-actions">
        {isEdit && (
          <button className="btn btn-danger" onClick={handleDelete} disabled={saving}>
            Delete Deal
          </button>
        )}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          <button className="btn btn-secondary" onClick={() => navigate(isEdit ? `/deals/${id}` : '/deals')} disabled={saving}>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={handleSubmit} disabled={saving}>
            {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Deal'}
          </button>
        </div>
      </div>
    </div>
  );
}
